import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSubscription } from "../../../context/SubscriptionContext";
import { initiatePayment } from "../../../services/paymentsApi";
import styles from "./SubscriptionBanner.module.css";

const SUBSCRIPTION_PATH = "/settings/subscription";

/**
 * Top-of-page banner that surfaces subscription-renewal prompts.
 *
 * Renders:
 *  - Red (non-dismissible) when status === EXPIRED or the paid period has already lapsed.
 *  - Yellow + urgent (non-dismissible) when status === PAYMENT_FAILED, or expiry ≤ 3 days.
 *  - Yellow (dismissible per session) when expiry ≤ 7 days.
 *  - Nothing otherwise.
 */
const SubscriptionBanner = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isExpired, isExpiringSoon, isUrgent, hasFailedPayment, daysUntilExpiry, subscription } =
    useSubscription();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("gredus.subscriptionBannerDismissed") === "1"
  );
  const [overLimitDismissed, setOverLimitDismissed] = useState(
    () => sessionStorage.getItem("gredus.studentOverLimitBannerDismissed") === "1"
  );
  const [renewing, setRenewing] = useState(false);

  // Stock-meter over-limit state: a tenant who downgraded below their live student count keeps
  // every existing student working, but cannot add more. That state is deliberate and
  // non-destructive — this banner is what makes it legible instead of a mystery error on the
  // "add student" button. Money banners (expiry / failed payment) take precedence.
  const studentUsage = (subscription?.usage || []).find((u) => u.limitCode === "STUDENT_COUNT_LIMIT");
  const studentsOverLimit =
    studentUsage && studentUsage.limit != null && studentUsage.limit !== -1 && studentUsage.used > studentUsage.limit
      ? studentUsage
      : null;

  const showMoneyBanner = (isExpired || isExpiringSoon || hasFailedPayment) && !(dismissed && !isExpired && !isUrgent);

  if (!showMoneyBanner && studentsOverLimit && !overLimitDismissed) {
    return (
      <div className={`${styles.banner} ${styles.warning}`} role="alert">
        <span className={styles.message}>
          {t("pages.subscription.banner.studentsOverLimit", {
            used: studentsOverLimit.used,
            limit: studentsOverLimit.limit,
          })}
        </span>
        <div className={styles.actions}>
          <button type="button" className={styles.cta} onClick={() => navigate(SUBSCRIPTION_PATH)}>
            {t("pages.subscription.banner.viewPlans")}
          </button>
          <button
            type="button"
            className={styles.dismiss}
            onClick={() => {
              sessionStorage.setItem("gredus.studentOverLimitBannerDismissed", "1");
              setOverLimitDismissed(true);
            }}
            aria-label={t("pages.subscription.banner.dismiss")}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  if (!isExpired && !isExpiringSoon && !hasFailedPayment) return null;
  if (dismissed && !isExpired && !isUrgent) return null;

  const handleDismiss = () => {
    sessionStorage.setItem("gredus.subscriptionBannerDismissed", "1");
    setDismissed(true);
  };

  // "Renew" must actually start the renewal — merely navigating to the subscription page is a no-op
  // when the user is already there. Only an existing paid plan can be renewed in one click; trials
  // (or an unknown state) have no plan to charge, so send them to the page to choose one. Any
  // failure also falls back to the page.
  const handleRenew = async () => {
    const status = subscription?.status;
    const canRenewDirectly =
      status === "ACTIVE" || status === "PAYMENT_FAILED" || status === "EXPIRED";
    if (!canRenewDirectly) {
      navigate(SUBSCRIPTION_PATH);
      return;
    }
    try {
      setRenewing(true);
      const res = await initiatePayment({
        purpose: "RENEWAL",
        returnPath: SUBSCRIPTION_PATH,
        language: i18n.language,
      });
      sessionStorage.setItem("gredus.kapitalPaymentTxId", String(res.paymentTransactionId));
      sessionStorage.setItem("gredus.kapitalReturnPath", SUBSCRIPTION_PATH);
      window.location.href = res.redirectUrl;
    } catch (e) {
      toast.error(t("pages.subscription.banner.renewError"), { toastId: "banner-renew-error" });
      setRenewing(false);
      navigate(SUBSCRIPTION_PATH);
    }
  };

  // A yearly plan is a prepaid term, not a rolling subscription — say so. Its countdown runs
  // against paidThrough (SubscriptionContext), and the one-click renew below already does the
  // right thing: a same-plan RENEWAL of a yearly plan extends the term by another 12 months.
  const isLicense = subscription?.billingType === "YEARLY";
  let message;
  if (isExpired) {
    message = t(isLicense ? "pages.subscription.banner.licenseExpired" : "pages.subscription.banner.expired");
  } else if (hasFailedPayment) {
    message = t("pages.subscription.banner.paymentFailed");
  } else if (daysUntilExpiry !== null) {
    if (daysUntilExpiry < 0) message = t(isLicense ? "pages.subscription.banner.licenseLapsed" : "pages.subscription.banner.lapsed");
    else if (daysUntilExpiry === 0) message = t(isLicense ? "pages.subscription.banner.licenseExpiresToday" : "pages.subscription.banner.expiresToday");
    else message = t(isLicense ? "pages.subscription.banner.licenseExpiresInDays" : "pages.subscription.banner.expiresInDays", { days: daysUntilExpiry });
  } else {
    message = t("pages.subscription.banner.renewGeneric");
  }

  const isPastDue = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const variantClass = isExpired || isPastDue ? styles.danger : styles.warning;

  return (
    <div className={`${styles.banner} ${variantClass}`} role="alert">
      <span className={styles.message}>{message}</span>
      <div className={styles.actions}>
        <button type="button" className={styles.cta} onClick={handleRenew} disabled={renewing}>
          {isExpired
            ? t("pages.subscription.banner.renewNow")
            : t("pages.subscription.banner.renew")}
        </button>
        {!isExpired && !isUrgent && (
          <button
            type="button"
            className={styles.dismiss}
            onClick={handleDismiss}
            aria-label={t("pages.subscription.banner.dismiss")}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionBanner;
